import { AppRoutes } from '@/utils';
import { useCallback, useState } from 'react';

const REDIRECT_KEY = 'redirect-after-login';
const REDIRECT_ORIGIN = 'https://placeholder.invalid';
const BLOCKED_PATHS = new Set<string>([AppRoutes.LOGIN, AppRoutes.ACCESS_DENIED]);

// A stored redirect must be a same-origin, in-app path that is NOT the auth wall. The Hosted-UI OAuth
// callback always lands on root (`/?code=...&state=...`; redirectSignIn is `origin + '/'` in aws-exports),
// so rejecting root also blocks replay of the single-use `code` — revisit this if the redirect URI ever
// stops being root. `?redirect=` is user-controllable, so resolving against a placeholder origin and
// comparing it blocks open-redirects (encoded scheme/host, protocol-relative and backslash authority tricks).
export const isSafeRedirect = (value: string | null | undefined): value is string => {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(value, REDIRECT_ORIGIN);
  } catch {
    return false;
  }

  if (url.origin !== REDIRECT_ORIGIN) {
    return false;
  }

  const pathname = url.pathname.replace(/\/+$/, '') || '/';

  return pathname !== '/' && !BLOCKED_PATHS.has(pathname);
};

function useRedirectRoute() {
  const getRedirect = useCallback(() => {
    return window.localStorage.getItem(REDIRECT_KEY);
  }, []);

  const setRedirect = useCallback((dest: string | null) => {
    if (isSafeRedirect(dest)) {
      window.localStorage.setItem(REDIRECT_KEY, dest);
    }
  }, []);

  const clearRedirect = useCallback(() => {
    window.localStorage.removeItem(REDIRECT_KEY);
  }, []);

  const [state] = useState({
    getRedirect,
    setRedirect,
    clearRedirect,
  });

  return state;
}

export default useRedirectRoute;
